<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { formatNumber } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';

import ConversationBasicFilter from './widgets/conversation/ConversationBasicFilter.vue';
import SwitchLayout from 'dashboard/routes/dashboard/conversation/search/SwitchLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useEmailSync } from 'dashboard/composables/useEmailSync';

const props = defineProps({
  pageTitle: { type: String, required: true },
  hasAppliedFilters: { type: Boolean, required: true },
  hasActiveFolders: { type: Boolean, required: true },
  activeStatus: { type: String, required: true },
  isOnExpandedLayout: { type: Boolean, required: true },
  conversationStats: { type: Object, required: true },
  phoneSearchOpen: { type: Boolean, default: false },
  hasPhoneSearch: { type: Boolean, default: false },
  isListLoading: { type: Boolean, required: true },
  showEmailSync: { type: Boolean, default: false },
  emailSyncInboxId: { type: [String, Number], default: null },
});

const emit = defineEmits([
  'addFolders',
  'deleteFolders',
  'resetFilters',
  'basicFilterChange',
  'filtersModal',
  'resolveAll',
  'togglePhoneSearch',
]);

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();
const { isSyncing: isEmailSyncing, syncEmails } = useEmailSync();
const isSyncingEmails = computed(() => isEmailSyncing(props.emailSyncInboxId));
const emailSyncLabel = computed(() =>
  props.emailSyncInboxId ? t('EMAIL_SYNC.LABEL_INBOX') : t('EMAIL_SYNC.LABEL')
);

const onBasicFilterChange = (value, type) => {
  emit('basicFilterChange', value, type);
};

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return props.hasAppliedFilters || props.hasActiveFolders;
});

const allCount = computed(() => props.conversationStats?.allCount || 0);
const formattedAllCount = computed(() => formatNumber(allCount.value));

const toggleConversationLayout = () => {
  const { LAYOUT_TYPES } = wootConstants;
  const {
    conversation_display_type: conversationDisplayType = LAYOUT_TYPES.CONDENSED,
  } = uiSettings.value;
  const newViewType =
    conversationDisplayType === LAYOUT_TYPES.CONDENSED
      ? LAYOUT_TYPES.EXPANDED
      : LAYOUT_TYPES.CONDENSED;
  updateUISettings({
    conversation_display_type: newViewType,
    previously_used_conversation_display_type: newViewType,
  });
};
</script>

<template>
  <div
    class="flex min-h-16 items-center justify-between gap-2 border-b border-n-weak bg-n-solid-1 px-3 py-2"
    :class="{
      'border-b border-n-strong': hasAppliedFiltersOrActiveFolders,
    }"
  >
    <div class="flex min-w-0 items-center gap-2.5">
      <span class="chat-list-header__icon">
        <span
          class="size-4.5"
          :class="
            showEmailSync ? 'i-hugeicons-mail-01' : 'i-lucide-messages-square'
          "
        />
      </span>
      <div class="flex min-w-0 items-center">
        <h1
          class="text-base font-medium truncate text-n-slate-12"
          :title="pageTitle"
        >
          {{ pageTitle }}
        </h1>
        <span
          v-if="
            allCount > 0 && hasAppliedFiltersOrActiveFolders && !isListLoading
          "
          class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
          :title="allCount"
        >
          {{ formattedAllCount }}
        </span>
        <span
          v-if="!hasAppliedFiltersOrActiveFolders"
          class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
        >
          {{ $t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${activeStatus}.TEXT`) }}
        </span>
      </div>
    </div>
    <div class="flex items-center gap-1">
      <NextButton
        v-if="showEmailSync"
        icon="i-hugeicons-refresh"
        :aria-label="emailSyncLabel"
        :title="emailSyncLabel"
        :aria-busy="isSyncingEmails ? 'true' : undefined"
        :disabled="isSyncingEmails"
        :class="isSyncingEmails && '[&_span]:animate-spin'"
        slate
        xs
        faded
        @click="syncEmails(emailSyncInboxId)"
      />
      <NextButton
        icon="i-hugeicons-search-01"
        :aria-label="$t('CHAT_LIST.PHONE_FILTER')"
        :title="$t('CHAT_LIST.PHONE_FILTER')"
        :aria-expanded="phoneSearchOpen"
        aria-controls="conversation-phone-search"
        :class="
          (phoneSearchOpen || hasPhoneSearch) && '!bg-n-brand/10 !text-n-brand'
        "
        slate
        xs
        faded
        @click="emit('togglePhoneSearch')"
      />
      <template v-if="hasAppliedFilters && !hasActiveFolders">
        <div class="relative">
          <NextButton
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
            icon="i-lucide-save"
            slate
            xs
            faded
            @click="emit('addFolders')"
          />
          <div
            id="saveFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
        <NextButton
          v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
          icon="i-lucide-circle-x"
          ruby
          faded
          xs
          @click="emit('resetFilters')"
        />
      </template>
      <template v-if="hasActiveFolders">
        <div class="relative">
          <NextButton
            id="toggleConversationFilterButton"
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
            icon="i-lucide-pen-line"
            slate
            xs
            faded
            @click="emit('filtersModal')"
          />
          <div
            id="conversationFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
        <NextButton
          id="toggleConversationFilterButton"
          v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
          icon="i-lucide-trash-2"
          ruby
          xs
          faded
          @click="emit('deleteFolders')"
        />
      </template>
      <div v-else class="relative">
        <NextButton
          id="toggleConversationFilterButton"
          v-tooltip.right="$t('FILTER.TOOLTIP_LABEL')"
          icon="i-lucide-funnel"
          slate
          xs
          faded
          @click="emit('filtersModal')"
        />
        <div
          id="conversationFilterTeleportTarget"
          class="absolute z-50 mt-2"
          :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
        />
      </div>
      <ConversationBasicFilter
        v-if="!hasAppliedFiltersOrActiveFolders"
        :is-on-expanded-layout="isOnExpandedLayout"
        @change-filter="onBasicFilterChange"
      />
      <NextButton
        v-if="!hasAppliedFiltersOrActiveFolders"
        v-tooltip.top-end="$t('BULK_ACTION.RESOLVE_ALL_OPEN')"
        icon="i-lucide-check-check"
        slate
        xs
        faded
        @click="emit('resolveAll')"
      />
      <SwitchLayout
        :is-on-expanded-layout="isOnExpandedLayout"
        @toggle="toggleConversationLayout"
      />
    </div>
  </div>
</template>

<style scoped>
.chat-list-header__icon {
  @apply grid size-9 shrink-0 place-items-center rounded-xl bg-n-brand/10 text-n-brand;
}
</style>
